import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    visitDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('visitDate', formData.visitDate);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4pukdpmma3c958r81q0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', visitDate: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/f4db1a929132d799a6dcc418b0b1ab3b.jpeg"
            alt="1.5階建て住宅"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 w-full">
          <div className="text-left">
            <h1 className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-8 sm:mb-12 leading-tight px-4">
              平屋の快適さは<br />
              そのままに<br />
              予算の悩みを解消した<br />
              1.5階建て
            </h1>
            <a 
              href="#reservation" 
              className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 sm:px-16 py-4 sm:py-6 rounded-full text-lg sm:text-2xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-2xl hover:shadow-orange-500/50 whitespace-nowrap cursor-pointer ml-4"
            >
              モデルハウス見学予約
              <i className="ri-arrow-right-line text-xl sm:text-3xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Concept Section */}
      <section className="py-24 bg-gradient-to-b from-amber-50/50 to-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold text-orange-600 tracking-widest mb-4 uppercase">Concept</span>
            <h2 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-6">
              1.5階建ての<br className="sm:hidden" />コンセプト
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-10 rounded-3xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-2xl mb-6">
                <i className="ri-home-heart-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">平屋と二階建ての「いいとこどり」</h3>
              <p className="text-gray-600 leading-relaxed text-lg">平屋の快適さはそのままに、価格の悩みを解消した住宅です。</p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-2xl mb-6">
                <i className="ri-parent-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">親同居も考えた設計</h3>
              <p className="text-gray-600 leading-relaxed text-lg">子育て世代でも将来の親居住や老後の暮らしを考慮した住宅です。</p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-2xl mb-6">
                <i className="ri-eye-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">目が届く安心とプライバシーの両立</h3>
              <p className="text-gray-600 leading-relaxed text-lg">子育ての「目が届く安心とプライバシーの両立」を実現する設計です。</p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-2xl mb-6">
                <i className="ri-shield-check-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">バリアフリーの安全</h3>
              <p className="text-gray-600 leading-relaxed text-lg">老後の「バリアフリーの安全」を「適正予算」で叶えます。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Model House Gallery */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="mb-16">
            <span className="inline-block text-sm font-semibold text-orange-600 tracking-widest uppercase">Model House</span>
            <h2 className="text-5xl font-bold text-gray-900 mt-4 mb-2">湯沢市の</h2>
            <h3 className="text-6xl font-bold text-gray-900 italic">1.5階建て<br className="sm:hidden" />モデルハウス</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-rose-50 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 cursor-pointer">
              <div className="h-96">
                <img
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/f4db1a929132d799a6dcc418b0b1ab3b.jpeg"
                  alt="外観デザイン"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="bg-white p-6 text-center">
                <h4 className="text-xl font-bold text-gray-900">外観デザイン</h4>
              </div>
            </div>
            <div className="bg-emerald-50 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 cursor-pointer">
              <div className="h-96">
                <img
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/4e7aa7db4b48004af02c0ba80cdc39eb.jpeg"
                  alt="内装デザイン"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="bg-white p-6 text-center">
                <h4 className="text-xl font-bold text-gray-900">内装デザイン</h4>
              </div>
            </div>
            <div className="bg-green-50 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2">
              <div className="h-96">
                <img
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/d6bb7092f44f49648a58bba27d152bc2.jpeg"
                  alt="1.5階間取り図"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-white p-6 text-center">
                <h4 className="text-xl font-bold text-gray-900">キッチン設備</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gradient-to-b from-amber-50 to-orange-50/30">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold text-orange-600 tracking-widest mb-4 uppercase">Benefits</span>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">モデルハウスの特徴</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-10 rounded-3xl shadow-lg">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full mb-6">
                <i className="ri-snowy-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">雪おろしが楽で安全</h3>
              <p className="text-gray-600 leading-relaxed text-center text-lg">雪の転落リスクが軽減され、作業もラクに</p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-lg">
              <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mb-6">
                <i className="ri-wheelchair-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">バリアフリー安全設計</h3>
              <p className="text-gray-600 leading-relaxed text-center text-lg">階段がないバリアフリーの家事動線でお子様や高齢者も安心</p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-lg">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-100 rounded-full mb-6">
                <i className="ri-money-dollar-circle-line text-4xl text-amber-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">光熱費がお得に</h3>
              <p className="text-gray-600 leading-relaxed text-center text-lg">ワンフロアで空調効率が良く光熱費を抑えやすい</p>
            </div>
          </div>

          <div className="mt-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl p-12 text-center shadow-2xl">
            <h3 className="text-4xl font-bold text-white mb-6">今ならモデルハウス来場予約で</h3>
            <p className="text-6xl font-black text-white mb-4">クオカード500円</p>
          </div>
        </div>
      </section>

      {/* Floor Plans & Pricing */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold text-orange-600 tracking-widest mb-4 uppercase">Floor Plans & Pricing</span>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">参考プラン・価格</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-amber-50 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2">
              <div className="h-80">
                <img
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/f23f2562a3a1088874cf58d7f750205d.png"
                  alt="外観イメージ図"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="bg-white p-6 text-center">
                <h4 className="text-xl font-bold text-gray-900">外観イメージ図</h4>
              </div>
            </div>
            <div className="bg-blue-50 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2">
              <div className="h-80 bg-white flex items-center justify-center p-4">
                <img
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/d9bc3ab054094a8663da7fc69e9dbe37.jpeg"
                  alt="1階間取り図"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-white p-6 text-center">
                <h4 className="text-xl font-bold text-gray-900">1階間取り図(14.5坪)</h4>
              </div>
            </div>
            <div className="bg-green-50 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2">
              <div className="h-80">
                <img
                  src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/583c795f5dd37559c20e0c398e940d6a.jpeg"
                  alt="1.5階間取り図"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="bg-white p-6 text-center">
                <h4 className="text-xl font-bold text-gray-900">1.5階間取り図(7.5坪)</h4>
              </div>
            </div>
            <div className="bg-orange-50 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2">
              <div className="h-80 flex flex-col items-center justify-center p-8">
                <div className="text-center mb-6">
                  <p className="text-gray-600 text-lg mb-2">本体価格</p>
                  <p className="text-5xl font-black text-orange-600 mb-1">1,485</p>
                  <p className="text-2xl font-bold text-gray-900">万円(税込)</p>
                </div>
                <div className="w-full border-t-2 border-gray-200 pt-6">
                  <p className="text-gray-600 text-base mb-2 text-center">お支払い例</p>
                  <p className="text-3xl font-bold text-gray-900 text-center mb-1">月々 37,199円</p>
                  <p className="text-sm text-gray-500 text-center">40年ローン・金利3年固定0.95%の場合</p>
                </div>
              </div>
              <div className="bg-white p-6 text-center">
                <h4 className="text-xl font-bold text-gray-900">価格・お支払い例</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recommended Section */}
      <section className="py-24 bg-amber-50/50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center bg-orange-600 rounded-lg">
                <i className="ri-star-line text-white text-lg"></i>
              </div>
              <span className="text-sm font-semibold text-orange-600 tracking-widest uppercase">Point</span>
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">1.5階建ては、こんな方におすすめ</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            <div className="bg-white p-10 rounded-3xl shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-home-4-line text-5xl text-gray-800"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">01. 平屋が欲しいけど、予算が合わない方</h3>
              <p className="text-gray-600 leading-relaxed text-lg">子供部屋を1.5階にすることで、基礎部分と屋根の面積が減り、建築費が大幅に削減されます。予算を抑えながらも、メインの生活動線は平屋と同じ快適性を実現します。</p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-stairs-line text-5xl text-gray-800"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">02. 親との同居や老後を考えた家にしたい方</h3>
              <p className="text-gray-600 leading-relaxed text-lg">1.5階建ては、主な日常の生活動線はすべて1階で完結するバリアフリー設計。親御様とのお住まいや老後の暮らしも安心です。</p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-heart-line text-5xl text-gray-800"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">03. 子育て世代でプライバシーを配慮した平屋がほしい方</h3>
              <p className="text-gray-600 leading-relaxed text-lg">1.5階に子供部屋を設けることで、ご家族のプライバシーを守ります。</p>
            </div>
          </div>

          {/* Q&A */}
          <div className="mt-20">
            <div className="mb-12">
              <p className="text-sm text-gray-500 mb-2">よくあるご質問</p>
              <h3 className="text-4xl font-bold text-gray-900 mb-2">1.5階建てについて</h3>
              <p className="text-3xl font-bold text-gray-900 italic">Q&A</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-stone-50 p-10 rounded-3xl">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-600 rounded-full mb-6">
                  <span className="text-white text-xl font-bold">Q</span>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">1.5階建てにすることで、平屋と比べて建築費はどのくらい抑えられますか？</h4>
                <p className="text-gray-600 leading-relaxed">一般的な平屋と比べても、1.5階建ては基礎や屋根の面積が小さくなることで、建築費が100万円単位で削減することが可能です。</p>
              </div>
              <div className="bg-stone-50 p-10 rounded-3xl">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-600 rounded-full mb-6">
                  <span className="text-white text-xl font-bold">Q</span>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">1.5階建ての階段は、子どもが小さいうちは危険ではありませんか？</h4>
                <p className="text-gray-600 leading-relaxed">階段には、初期段階から取り外し可能なベビーゲートの設置を前提とした壁の基礎補強などを施します。階段勾配も緩やかに設計するため、成長に合わせて安全性を確保できます。</p>
              </div>
              <div className="bg-stone-50 p-10 rounded-3xl">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-600 rounded-full mb-6">
                  <span className="text-white text-xl font-bold">Q</span>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">1.5階のスペースを将来、子ども部屋から夫婦の趣味の部屋に変更できますか？</h4>
                <p className="text-gray-600 leading-relaxed">はい、可能です。1.5階を最初から多目的利用を前提に設計し、間仕切り壁を撤去しやすい構造になっておりますので、用途の変更に柔軟に対応できます。</p>
              </div>
              <div className="bg-stone-50 p-10 rounded-3xl">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-600 rounded-full mb-6">
                  <span className="text-white text-xl font-bold">Q</span>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">雪の多い地域で、1.5階建ての雪おろしについて不安があります。本当に楽で安全なのでしょうか？</h4>
                <p className="text-gray-600 leading-relaxed">はい、一般的な2階建てと比較しても屋根の面積が少ないため、雪下ろし作業が安全でラクになります。また、秋田の積雪量と雪質を考慮し、自然落雪を促進する適切な屋根勾配を設計に組み込むことでお客様のご負担を軽減できます。</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Voice */}
      <section className="py-24 bg-stone-100">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center bg-orange-600 rounded-lg">
                <i className="ri-chat-quote-line text-white text-lg"></i>
              </div>
              <span className="text-sm font-semibold text-orange-600 tracking-widest uppercase">Voice</span>
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">お客様の声</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50/50 p-10 rounded-3xl shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-200 rounded-full overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/a81fc672f797f5d9dc5d31e1cd245262.jpg"
                    alt="大仙市・40代夫婦"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-lg">大仙市</p>
                  <p className="text-gray-600">40代夫婦</p>
                </div>
              </div>
              <div className="mb-4">
                <i className="ri-double-quotes-l text-4xl text-orange-400"></i>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg">親との同居計画で平屋を希望していましたが、予算が合わず諦めていました。1.5階建てにしたところ、経済的な条件を満たしつつ、今の生活に大変満足しています。</p>
            </div>
            <div className="bg-blue-50/50 p-10 rounded-3xl shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-200 rounded-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Young%20Japanese%20couple%20in%20their%20early%2030s%20smiling%20cheerfully%20happy%20portrait%20man%20and%20woman%20standing%20close%20together%20warm%20friendly%20expression%20simple%20clean%20background%20natural%20lighting%20professional%20photo%20different%20people&width=200&height=200&seq=customer-voice-yuzawa-young-002&orientation=squarish"
                    alt="湯沢市・40代夫婦"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-lg">湯沢市</p>
                  <p className="text-gray-600">40代夫婦</p>
                </div>
              </div>
              <div className="mb-4">
                <i className="ri-double-quotes-l text-4xl text-orange-400"></i>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg">子供が巣立つと、二階の部屋が使われなくなりますが、一階で暮らしが完結する設計だから、老後の心配もなくなりました。</p>
            </div>
            <div className="bg-blue-50/50 p-10 rounded-3xl shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-200 rounded-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Japanese%20couple%20in%20their%2040s%20smiling%20warmly%20happy%20portrait%20middle%20aged%20man%20and%20woman%20standing%20together%20mature%20friendly%20expression%20simple%20clean%20background%20natural%20lighting%20professional%20photo&width=200&height=200&seq=customer-voice-yokote-40s-004&orientation=squarish"
                    alt="横手市・50代夫婦"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-lg">横手市</p>
                  <p className="text-gray-600">50代夫婦</p>
                </div>
              </div>
              <div className="mb-4">
                <i className="ri-double-quotes-l text-4xl text-orange-400"></i>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg">適切な屋根勾配などを何度も打ち合わせしたので、1.5階建てでも雪下ろし作業が安全でラクになり安心しています。</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h2 className="text-6xl font-black text-gray-900 mb-6">モデルハウス見学予約で</h2>
          <p className="text-3xl text-gray-500 mb-12">今ならクオカード500円プレゼント</p>
          <a 
            href="#reservation" 
            className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-16 py-6 rounded-full text-2xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-2xl hover:shadow-orange-500/50 whitespace-nowrap cursor-pointer"
          >
            今すぐ予約する
            <i className="ri-arrow-right-line text-3xl"></i>
          </a>
        </div>
      </section>

      {/* Reservation Form */}
      <section id="reservation" className="py-24 bg-amber-50/30">
        <div className="max-w-3xl mx-auto px-8">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">モデルハウス見学予約フォーム</h2>
            <p className="text-gray-600 text-lg">以下のフォームにご記入ください</p>
          </div>
          <form 
            id="reservation-form" 
            data-readdy-form 
            onSubmit={handleSubmit} 
            className="bg-white rounded-3xl shadow-xl p-12"
          >
            <div className="space-y-8">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-600 mb-2">お名前 *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full border-b-2 border-gray-300 focus:border-orange-500 outline-none py-3 text-lg transition-colors"
                  placeholder="山田 太郎"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-600 mb-2">電話番号 *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full border-b-2 border-gray-300 focus:border-orange-500 outline-none py-3 text-lg transition-colors"
                  placeholder="090-1234-5678"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-600 mb-2">メールアドレス *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full border-b-2 border-gray-300 focus:border-orange-500 outline-none py-3 text-lg transition-colors"
                  placeholder="example@email.com"
                />
              </div>
              <div>
                <label htmlFor="visitDate" className="block text-sm font-semibold text-gray-600 mb-2">見学予約日時 *</label>
                <input
                  type="datetime-local"
                  id="visitDate"
                  name="visitDate"
                  value={formData.visitDate}
                  onChange={handleChange}
                  required
                  className="w-full border-b-2 border-gray-300 focus:border-orange-500 outline-none py-3 text-lg transition-colors"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-600 mb-2">ご質問、ご要望</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={5}
                  className="w-full border-b-2 border-gray-300 focus:border-orange-500 outline-none py-3 text-lg transition-colors resize-none"
                  placeholder="ご質問やご要望をお聞かせください（500文字以内）"
                />
                <p className="text-sm text-gray-500 mt-2 text-right">{formData.message.length} / 500</p>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-5 rounded-full text-xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '予約を確定する'}
              </button>
              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-500 text-green-700 px-6 py-4 rounded-2xl text-center">
                  <i className="ri-check-line text-2xl mr-2"></i>
                  ご予約ありがとうございます。担当者より折り返しご連絡いたします。
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-500 text-red-700 px-6 py-4 rounded-2xl text-center">
                  <i className="ri-error-warning-line text-2xl mr-2"></i>
                  送信に失敗しました。もう一度お試しください。
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* Company Info & Map */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">会社情報</h2>
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">運営会社</p>
                  <p className="text-2xl font-bold text-gray-900">湯沢開発株式会社</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">代表者</p>
                  <p className="text-xl text-gray-900">高橋正明</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">所在地</p>
                  <p className="text-xl text-gray-900">〒012-0844 秋田県湯沢市田町2-1-3</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">電話番号</p>
                  <p className="text-xl text-gray-900">0183-73-7125</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">定休日</p>
                  <p className="text-xl text-gray-900">水曜日</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">創業</p>
                  <p className="text-xl text-gray-900">1951年（昭和26年創業）</p>
                </div>
                <div className="pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600 leading-relaxed">宅地建物取引業許可：秋田県知事免許(17)第43号</p>
                  <p className="text-sm text-gray-600 leading-relaxed">建設業登録：秋田県知事許可（般-4）第80084号</p>
                  <p className="text-sm text-gray-600 leading-relaxed">一級建築士事務所：秋田県知事登録 第16-110A-0250号</p>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">モデルハウス案内図</h2>
              <div className="rounded-3xl overflow-hidden shadow-xl h-96">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1839.4744671621058!2d140.48342040318812!3d39.16073816029207!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sja!2sjp!4v1765093627269!5m2!1sja!2sjp"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="モデルハウス案内図"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-emerald-900 to-emerald-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div>
              <p className="text-gray-300 leading-relaxed text-sm">創業1951年、75年以上の実績</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold tracking-widest mb-6 uppercase text-gray-300">連絡先</h4>
              <div className="space-y-3">
                <p className="text-white text-lg">0183-73-7125</p>
                <p className="text-gray-300 text-sm">定休日：水曜日</p>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold tracking-widest mb-6 uppercase text-gray-300">Menu</h4>
              <div className="space-y-3">
                <a href="#reservation" className="block text-white hover:text-orange-300 transition-colors">モデルハウス見学予約</a>
                <a href="#contact" className="block text-white hover:text-orange-300 transition-colors">会社情報</a>
                <a href="https://www.y-kaihatsu.co.jp/" target="_blank" rel="noopener noreferrer" className="block text-white hover:text-orange-300 transition-colors">ホームページ</a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold tracking-widest mb-6 uppercase text-gray-300">Company</h4>
              <div className="space-y-3">
                <p className="text-white">湯沢開発株式会社</p>
                <p className="text-gray-300 text-sm">秋田県湯沢市田町2-1-3</p>
              </div>
            </div>
          </div>
          <div className="border-t border-emerald-700 pt-12">
            <p className="text-gray-400 text-sm text-center">Copyright © 2025 湯沢開発株式会社 All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
